import './typings/underscore';
import {Axis, Position, TimelineAxis} from './Axis';
import {RenderContext} from './RenderContext';
import {NumberUtils} from './Utils';
import {Series} from './Series';
import * as _ from 'underscore/underscore';

export interface IScale {
	scale(axis: Axis, context: RenderContext): {
		scaleFactor: number,	//scale-factor for the axis
		min?: number,	//new minimum if modified
		max?: number	//new maximum if modified
		};
}

export class Scale implements IScale {

	public scale(axis: Axis, context: RenderContext): {scaleFactor: number, min?: number, max?: number} {
		return {scaleFactor: 1};
	}

	protected getPlotSize(axis: Axis, context: RenderContext) {
		var layout = context.getComponentLayout();
		if(axis.position === Position.BOTTOM ||
			axis.position === Position.TOP) {
			return layout.plot.width;
		}
		if(axis.position === Position.LEFT ||
			axis.position === Position.RIGHT) {
			return layout.plot.height;
		}
		throw "Invalid axis position: '" + axis.position + "'";
	}
}

export class FixedScale extends Scale {

	public minimum: number;
	public maximum: number;

	constructor(minimum: number, maximum: number) {
		super();
		this.minimum = minimum;
		this.maximum = maximum;
	}

	public scale(axis: Axis, context: RenderContext) {

		var plotSize = this.getPlotSize(axis, context);

		return {
			scaleFactor: plotSize / ( (this.maximum - this.minimum) || 1),
			min: this.minimum,
			max: this.maximum
		}
	}

}

export class FixedDisplayPeriodScale extends Scale {

	public scale(axis: Axis, context: RenderContext) {

		var plotSize = this.getPlotSize(axis, context);

		var displayPeriod = (axis instanceof TimelineAxis) ?
			axis.getDisplayPeriod() : (axis.maximum - axis.minimum);

		return {
			scaleFactor: plotSize / ( displayPeriod || 1)
		}
	}

}

export class RatioScale extends Scale {

	public static PX_PER_CM = 38;

	private scaleFactor: number = 1;

	public constructor(domainValue: number, pxValue: number) {
		super();
		this.scaleFactor = pxValue / domainValue;
	}

	public scale(axis: Axis, context: RenderContext) {

		var plotSize = this.getPlotSize(axis, context);
		var min = axis.minimum;
		var max = min +  plotSize / this.scaleFactor;

		return {
			scaleFactor: this.scaleFactor,
			min: min,
			max: max
		}
	}

}

export interface AutoScaleOptions {
	keepPrevious?: boolean,
	niceRound?: boolean,
	divisor?: number
}

export class AutoScale extends Scale {

	private static DEFAULT_MIN = 0;
	private static DEFAULT_MAX = 100;

	public constructor(public options: AutoScaleOptions) {
		super();
	}

	public scale(axis: Axis, context: RenderContext) {

		var min = this.minimum(context.seriesConfiguration.series, axis.position);
		var max = this.maximum(context.seriesConfiguration.series, axis.position);
		var space = 0.1 * (max - min);
		min = (min == 0 ) ? 0 : min - space;
		max = (max == 0 ) ? 0 : max + space;

		var plotSize = this.getPlotSize(axis, context);

		if(this.options.keepPrevious) {
			min = Math.min(axis.minimum, min);
			max = Math.max(axis.maximum, max);
		}

		if(isFinite(min) == false) {
			min = AutoScale.DEFAULT_MIN;
		}

		if(isFinite(max) == false) {
			max = AutoScale.DEFAULT_MAX;
		}

		if(this.options.niceRound) {
			var actualRange = max - min;
			min = NumberUtils.niceFloorWithPrecision(min, actualRange);
			max = NumberUtils.niceRoundWithPrecision(max, actualRange);
		}

		if(this.options.divisor > 0) {
			min = NumberUtils.divisibleRound(min, this.options.divisor);
			max = NumberUtils.divisibleRound(max, this.options.divisor);
		}

		return {
			scaleFactor: plotSize / ( (max - min) || 1),
			min: min,
			max: max
		}
	}

	private minimum(series: Series<any>[], position: Position): number{
		return _.min(
				_.map(series,
					( series ) => {
						var valueProvider =
						(position == Position.BOTTOM || position == Position.TOP) ?
							series.valueProvider.getX : series.valueProvider.getY;

						return valueProvider(_.min(series.values, valueProvider));
						})
				);
	}

	private maximum(series: Series<any>[], position: Position): number {
		return _.max(
				_.map(series,
					( series ) => {
						var valueProvider =
						(position == Position.BOTTOM || position == Position.TOP) ?
							series.valueProvider.getX : series.valueProvider.getY;

						return valueProvider(_.max(series.values, valueProvider));
						})
				);
	}
}
